import { useMemo } from "react";

import { DEFAULT_REGISTERED_TOKEN_BALANCE, ENV_TOKEN_LIMITS } from "../config/env-token-config";
import { useAuthStore } from "../stores/auth";
import { useTokenStore } from "../stores/token";

type SubscriptionTier = "FREE" | "BASIC" | "PROFESSIONAL" | "ENTERPRISE";
type ComputedUserType = "GUEST" | "FREE" | "TOKEN_USER" | "BASIC" | "PROFESSIONAL" | "ENTERPRISE";

/**
 * Custom hook that provides computed user access information
 * Uses existing SubscriptionTier and determines user type based on business logic
 * Now uses environment-based token configuration from .env file
 */
export const useUserAccess = () => {
  const { user, subscriptionTier } = useAuthStore();
  const { tokenBalance } = useTokenStore();

  // Computed user type based on subscription tier and token purchase history
  const userType: ComputedUserType = useMemo(() => {
    // No user = Guest
    if (!user) return "GUEST";

    // If user has FREE tier, check if they have ever purchased tokens
    if (subscriptionTier === "FREE") {
      // Detect token purchases by checking lifetime tokens (balance + used) vs initial allocation.
      // A user who bought 1000 tokens but spent 500 still has lifetime > initial, so stays TOKEN_USER.
      const currentBalance = tokenBalance ?? 0;
      const aiTokensUsed = user.aiTokensUsed ?? 0;
      const lifetimeTokens = currentBalance + aiTokensUsed;
      const hasPurchasedTokens = lifetimeTokens > DEFAULT_REGISTERED_TOKEN_BALANCE;

      return hasPurchasedTokens ? "TOKEN_USER" : "FREE";
    }

    // For all other tiers, return the tier as user type
    return subscriptionTier;
  }, [user, subscriptionTier, tokenBalance]);

  // Access level helpers
  const accessLevel = useMemo(
    () => ({
      isGuest: userType === "GUEST",
      isFree: userType === "FREE",
      isTokenUser: userType === "TOKEN_USER",
      isBasic: userType === "BASIC",
      isProfessional: userType === "PROFESSIONAL",
      isEnterprise: userType === "ENTERPRISE",

      // Feature access
      hasSubscription: ["BASIC", "PROFESSIONAL", "ENTERPRISE"].includes(userType),
      hasPremiumFeatures: ["BASIC", "PROFESSIONAL", "ENTERPRISE"].includes(userType),
      canUseAIFeatures: userType !== "GUEST", // All non-guest users can use AI features if they have tokens
      canPurchaseTokens: userType !== "GUEST", // Only registered users can purchase tokens

      // Token limits based on user type - now using environment config from .env
      getTokenLimit: () => {
        switch (userType) {
          case "GUEST": {
            return 0;
          }
          case "FREE": {
            return ENV_TOKEN_LIMITS.FREE;
          }
          case "TOKEN_USER": {
            return Infinity; // Can purchase unlimited
          }
          case "BASIC": {
            return ENV_TOKEN_LIMITS.BASIC;
          }
          case "PROFESSIONAL": {
            return ENV_TOKEN_LIMITS.PROFESSIONAL;
          }
          case "ENTERPRISE": {
            return ENV_TOKEN_LIMITS.ENTERPRISE;
          }
          default: {
            return 0;
          }
        }
      },
    }),
    [userType],
  );

  return {
    userType,
    subscriptionTier,
    tokenBalance: tokenBalance ?? 0,
    user,
    ...accessLevel,
  };
};
